// 散布図の設定UIコンポーネント

import React, { useState } from 'react';
import { GraphConfigProps, ScatterPlotConfig } from '../../../../types/graph-builder.types';
import { createColumnId, ReferenceLine } from '../../../../types';
import { getIntervalRatioColumns, getCategoricalColumns } from '../../../utils/graph-builder-utils';
import { useNotificationStore } from '../../../stores/notificationStore';
import { ContextHelpButtonSimple } from '../../ContextHelp';
import * as styles from '../../../styles/components/graph-builder.css.ts';

export function ScatterPlotConfigComponent({
  config,
  dataset,
  onChange
}: GraphConfigProps<ScatterPlotConfig>) {
  const [activeTab, setActiveTab] = useState<'basic' | 'reference' | 'density'>('basic');

  // interval/ratio尺度の数値列のみを取得（散布図用）
  const numericColumns = getIntervalRatioColumns(dataset);
  // カテゴリカル列を取得（色分け用）
  const categoricalColumns = getCategoricalColumns(dataset);

  // データセットサイズを取得
  const datasetSize = dataset.rowCount || 0;

  const handleXColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      xColumn: createColumnId(e.target.value)
    });
  };

  const handleYColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      yColumn: createColumnId(e.target.value)
    });
  };

  const handleColorColumnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      colorColumn: e.target.value ? createColumnId(e.target.value) : undefined
    });
  };

  // Density設定のハンドラー
  const handleDisplayModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      displayMode: e.target.value as 'points' | 'density' | 'both' | 'points-with-density'
    });
  };

  const handleDensityVisualizationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      densityVisualization: e.target.value as 'contour-only' | 'filled-only' | 'filled-with-lines'
    });
  };

  const handleBandwidthModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      densityBandwidth: e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value) || 'auto'
    });
  };

  const handleContourLevelsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 5 && value <= 30) {
      onChange({
        ...config,
        contourLevels: value
      });
    }
  };

  const handleDensityColorScaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...config,
      densityColorScale: e.target.value as 'YlOrRd' | 'Blues' | 'Greens' | 'Greys' | 'Purples' | 'Viridis' | 'Plasma'
    });
  };

  // Reference Lines管理
  const [showReferenceLineUI, setShowReferenceLineUI] = useState(false);
  
  const handleAddReferenceLine = (line: ReferenceLine) => {
    const currentLines = config.referenceLines || [];
    onChange({
      ...config,
      referenceLines: [...currentLines, line]
    });
  };
  
  const handleRemoveReferenceLine = (index: number) => {
    const currentLines = config.referenceLines || [];
    onChange({
      ...config,
      referenceLines: currentLines.filter((_, i) => i !== index)
    });
  };

  return (
    <div className={styles.tabContainer}>
      {/* タブヘッダー */}
      <div className={styles.tabHeader}>
        <button
          className={`${styles.tab} ${activeTab === 'basic' ? styles.active : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'density' ? styles.active : ''}`}
          onClick={() => setActiveTab('density')}
        >
          Density
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'reference' ? styles.active : ''}`}
          onClick={() => setActiveTab('reference')}
        >
          Reference Lines
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className={styles.tabContent}>
        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div>
            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>X-Axis</label>
              <select
                className={styles.settingSelect}
                value={config.xColumn || ''}
                onChange={handleXColumnChange}
                data-testid="scatter-x-axis-select"
              >
                <option value="">Select X column</option>
                {numericColumns.length === 0 ? (
                  <option value="" disabled>No numeric columns with interval/ratio scale</option>
                ) : (
                  numericColumns.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Y-Axis</label>
              <select
                className={styles.settingSelect}
                value={config.yColumn || ''}
                onChange={handleYColumnChange}
                data-testid="scatter-y-axis-select"
              >
                <option value="">Select Y column</option>
                {numericColumns.length === 0 ? (
                  <option value="" disabled>No numeric columns with interval/ratio scale</option>
                ) : (
                  numericColumns.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className={styles.settingItem}>
              <label className={styles.settingLabel}>Color (Optional)</label>
              <select 
                className={styles.settingSelect}
                value={config.colorColumn || ''}
                onChange={handleColorColumnChange}
              >
                <option value="">None</option>
                {categoricalColumns.length === 0 ? (
                  <option value="" disabled>No categorical columns available</option>
                ) : (
                  categoricalColumns.map(col => (
                    <option key={col.id} value={col.id}>
                      {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        )}

        {/* Density Tab */}
        {activeTab === 'density' && (
          <div>
            {/* データセットサイズに基づく推奨 */}
            {datasetSize > 0 && (
              <div className={styles.settingItem} style={{ marginBottom: '16px', padding: '8px', background: '#f0f9ff', borderRadius: '4px' }}>
                <p style={{ fontSize: '12px', color: '#0369a1', margin: 0 }}>
                  {datasetSize < 1000 && (
                    <><strong>Recommendation:</strong> Points only mode is best for datasets with &lt; 1,000 points.</>
                  )}
                  {datasetSize >= 1000 && datasetSize < 10000 && (
                    <><strong>Recommendation:</strong> Points + Density mode is recommended for datasets with 1,000-10,000 points.</>
                  )}
                  {datasetSize >= 10000 && (
                    <><strong>Recommendation:</strong> Density only mode is recommended for datasets with 10,000+ points.</>
                  )}
                </p>
              </div>
            )}

            {/* Display Mode */}
            <div className={styles.settingItem}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <label className={styles.settingLabel}>Display Mode</label>
                <ContextHelpButtonSimple
                  ariaLabel="Display mode help - points, density, or combined visualization"
                  helpContent={
                    <div>
                      <h3>Display Mode</h3>
                      <p>
                        Controls how data points and density visualization are displayed.
                      </p>
                      <h4>Available modes:</h4>
                      <ul>
                        <li><strong>Points only</strong>: Traditional scatter plot showing all individual points. Best for small datasets (&lt; 1,000 points) where points don't overlap.</li>
                        <li><strong>Density only</strong>: Shows only 2D kernel density estimation (KDE) contours without individual points. Recommended for large datasets (10,000+ points) where point overlap is severe.</li>
                        <li><strong>Points + Density (Outliers Only)</strong>: Combines density contours with low-density points (outliers). Shows overall patterns via density while highlighting unusual observations. Recommended for medium to large datasets (1,000-10,000 points).</li>
                        <li><strong>Points + Density (All Points)</strong>: Shows both density contours and all individual points. Useful when you want to see both the overall distribution and maintain ability to inspect individual data points.</li>
                      </ul>
                    </div>
                  }
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                  <input
                    type="radio"
                    name="displayMode"
                    value="points"
                    checked={(config.displayMode || 'points') === 'points'}
                    onChange={handleDisplayModeChange}
                    style={{ marginRight: '8px' }}
                  />
                  Points only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                  <input
                    type="radio"
                    name="displayMode"
                    value="density"
                    checked={config.displayMode === 'density'}
                    onChange={handleDisplayModeChange}
                    style={{ marginRight: '8px' }}
                  />
                  Density only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                  <input
                    type="radio"
                    name="displayMode"
                    value="both"
                    checked={config.displayMode === 'both'}
                    onChange={handleDisplayModeChange}
                    style={{ marginRight: '8px' }}
                  />
                  Points + Density (Outliers Only)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                  <input
                    type="radio"
                    name="displayMode"
                    value="points-with-density"
                    checked={config.displayMode === 'points-with-density'}
                    onChange={handleDisplayModeChange}
                    style={{ marginRight: '8px' }}
                  />
                  Points + Density (All Points)
                </label>
              </div>
            </div>

            {/* Density Options（displayModeが'density', 'both', 'points-with-density'の場合に表示） */}
            {config.displayMode && config.displayMode !== 'points' && (
              <>
                <div className={styles.settingItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <label className={styles.settingLabel}>Density Visualization</label>
                    <ContextHelpButtonSimple
                      ariaLabel="Density style help - contour lines, filled regions, or both"
                      helpContent={
                        <div>
                          <h3>Density Visualization Style</h3>
                          <p>
                            Controls the visual appearance of density contours.
                          </p>
                          <h4>Available styles:</h4>
                          <ul>
                            <li><strong>Contour lines only</strong>: Shows density as unfilled contour lines only. Provides a clean, minimal visualization emphasizing density boundaries. Good when you want to avoid visual clutter.</li>
                            <li><strong>Filled only</strong>: Shows density as filled regions without contour borders. Makes density magnitude more intuitive through color intensity. Best for emphasizing high vs. low density areas.</li>
                            <li><strong>Filled with lines (default)</strong>: Combines filled regions with contour borders. Provides both density magnitude (via color) and precise boundaries (via lines). Recommended for most use cases as it provides maximum information.</li>
                          </ul>
                          <p><strong>Color scale</strong>: Yellow (low density) → Orange → Red (high density)</p>
                        </div>
                      }
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="radio"
                        name="densityVisualization"
                        value="contour-only"
                        checked={config.densityVisualization === 'contour-only'}
                        onChange={handleDensityVisualizationChange}
                        style={{ marginRight: '8px' }}
                      />
                      Contour lines only
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="radio"
                        name="densityVisualization"
                        value="filled-only"
                        checked={config.densityVisualization === 'filled-only'}
                        onChange={handleDensityVisualizationChange}
                        style={{ marginRight: '8px' }}
                      />
                      Filled only
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="radio"
                        name="densityVisualization"
                        value="filled-with-lines"
                        checked={(config.densityVisualization || 'filled-with-lines') === 'filled-with-lines'}
                        onChange={handleDensityVisualizationChange}
                        style={{ marginRight: '8px' }}
                      />
                      Filled with lines (default)
                    </label>
                  </div>
                </div>

                <div className={styles.settingItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <label className={styles.settingLabel}>Bandwidth</label>
                    <ContextHelpButtonSimple
                      ariaLabel="Bandwidth help - controls smoothness of density estimation"
                      helpContent={
                        <div>
                          <h3>Bandwidth</h3>
                          <p>
                            Controls the smoothness of the density estimation. Bandwidth determines how much neighboring points influence the density calculation at each location.
                          </p>
                          <h4>Settings:</h4>
                          <ul>
                            <li><strong>Auto (Scott's rule)</strong>: Automatically calculates optimal bandwidth based on your data using a statistically sound method. Recommended for most cases.</li>
                            <li><strong>Narrow (0.1-0.5)</strong>: Shows more local detail and variation. Use when you want to see fine-grained structure in the data, but may introduce noise.</li>
                            <li><strong>Medium (1.0)</strong>: Balanced between detail and smoothness.</li>
                            <li><strong>Wide (1.5-2.0)</strong>: Emphasizes broad patterns and smooths out local variations. Use when you want to see general trends rather than details.</li>
                          </ul>
                          <h4>When to adjust:</h4>
                          <p>
                            If the automatic bandwidth produces contours that are too smooth (hiding important structure) or too rough (showing too much noise), try manual adjustment.
                          </p>
                        </div>
                      }
                    />
                  </div>
                  <select
                    className={styles.settingSelect}
                    value={typeof config.densityBandwidth === 'number' ? config.densityBandwidth.toString() : 'auto'}
                    onChange={handleBandwidthModeChange}
                    data-testid="density-bandwidth-select"
                  >
                    <option value="auto">Auto (Scott's rule)</option>
                    <option value="0.1">0.1 (Narrow)</option>
                    <option value="0.5">0.5</option>
                    <option value="1.0">1.0 (Medium)</option>
                    <option value="1.5">1.5</option>
                    <option value="2.0">2.0 (Wide)</option>
                  </select>
                </div>

                <div className={styles.settingItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <label className={styles.settingLabel}>Contour Levels</label>
                    <ContextHelpButtonSimple
                      ariaLabel="Contour levels help - number of density bands to display"
                      helpContent={
                        <div>
                          <h3>Contour Levels</h3>
                          <p>
                            Controls how many contour lines/bands are drawn to represent the density distribution.
                          </p>
                          <h4>Guidelines:</h4>
                          <ul>
                            <li><strong>Fewer levels (5-8)</strong>: Shows broad density regions with clear separation between high and low density areas. Good for identifying major clusters.</li>
                            <li><strong>Medium levels (10-15, default: 10)</strong>: Balanced detail showing both major patterns and some gradual transitions. Recommended for most visualizations.</li>
                            <li><strong>More levels (16-30)</strong>: Shows fine gradations in density. Useful for detailed analysis but can make the plot visually busy.</li>
                          </ul>
                          <h4>How it works:</h4>
                          <p>
                            Contour levels are evenly spaced between the minimum and maximum density values in your data. More levels = more detailed density map, but potentially harder to read.
                          </p>
                        </div>
                      }
                    />
                  </div>
                  <input
                    type="number"
                    className={styles.settingInput}
                    value={config.contourLevels || 10}
                    onChange={handleContourLevelsChange}
                    min={5}
                    max={30}
                  />
                  <p style={{ fontSize: '11px', color: '#666', margin: '4px 0 0 0' }}>
                    Number of contour levels (5-30, default: 10)
                  </p>
                </div>

                <div className={styles.settingItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <label className={styles.settingLabel}>Color Scale</label>
                    <ContextHelpButtonSimple
                      ariaLabel="Color scale help - palette options for density visualization"
                      helpContent={
                        <div>
                          <h3>Density Color Scale</h3>
                          <p>
                            Controls the color palette used to visualize density values.
                          </p>
                          <h4>Available scales:</h4>
                          <ul>
                            <li><strong>YlOrRd (Yellow-Orange-Red, default)</strong>: Warm colors from yellow (low) to red (high). Good general-purpose scale.</li>
                            <li><strong>Blues</strong>: Cool blue gradient. Useful when warm colors are used for data points.</li>
                            <li><strong>Greens</strong>: Green gradient. Alternative cool color option.</li>
                            <li><strong>Greys</strong>: Grayscale from light to dark. Best for minimalist visualization or when points are already color-coded.</li>
                            <li><strong>Purples</strong>: Purple gradient. Another alternative cool color option.</li>
                            <li><strong>Viridis</strong>: Perceptually uniform blue-green-yellow. Colorblind-friendly.</li>
                            <li><strong>Plasma</strong>: Perceptually uniform purple-red-yellow. Colorblind-friendly.</li>
                          </ul>
                          <h4>Tip:</h4>
                          <p>
                            When using color mapping for points (Color column), choose a density color scale that doesn't conflict. For example:
                          </p>
                          <ul>
                            <li>If points use warm colors → use Blues, Greens, or Greys for density</li>
                            <li>If points use categorical colors → use Greys for density to avoid visual clutter</li>
                          </ul>
                        </div>
                      }
                    />
                  </div>
                  <select
                    className={styles.settingSelect}
                    value={config.densityColorScale || 'YlOrRd'}
                    onChange={handleDensityColorScaleChange}
                  >
                    <option value="YlOrRd">YlOrRd (Yellow-Orange-Red)</option>
                    <option value="Blues">Blues</option>
                    <option value="Greens">Greens</option>
                    <option value="Greys">Greys</option>
                    <option value="Purples">Purples</option>
                    <option value="Viridis">Viridis</option>
                    <option value="Plasma">Plasma</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        {/* Reference Lines Tab */}
        {activeTab === 'reference' && (
          <div>
            {/* 既存のReference Lines */}
            {config.referenceLines && config.referenceLines.length > 0 && (
              <div className={styles.referenceLinesList}>
                {config.referenceLines.map((line, index) => (
                  <div key={index} className={styles.referenceLineItem}>
                    <span className={styles.referenceLineDescription}>
                      {line.type === 'horizontal' && `Horizontal: y = ${line.value}`}
                      {line.type === 'vertical' && `Vertical: x = ${line.value}`}
                      {line.type === 'diagonal' && `Diagonal: y = x`}
                      {line.label && ` (${line.label})`}
                    </span>
                    <button
                      onClick={() => handleRemoveReferenceLine(index)}
                      className={styles.removeButton}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Reference Line UI */}
            {showReferenceLineUI ? (
              <ReferenceLineForm
                onAdd={handleAddReferenceLine}
                onCancel={() => setShowReferenceLineUI(false)}
              />
            ) : (
              <button
                onClick={() => setShowReferenceLineUI(true)}
                className={styles.addButton}
              >
                Add Reference Line
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Reference Line追加フォーム
function ReferenceLineForm({
  onAdd,
  onCancel
}: {
  onAdd: (line: ReferenceLine) => void;
  onCancel: () => void;
}) {
  const { error: showError } = useNotificationStore();
  const [type, setType] = useState<'horizontal' | 'vertical' | 'diagonal'>('horizontal');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [strokeStyle, setStrokeStyle] = useState<'solid' | 'dashed'>('solid');
  const [color, setColor] = useState('#999999');

  const handleSubmit = () => {
    const line: ReferenceLine = {
      type,
      label: label || undefined,
      style: {
        stroke: color,
        strokeDasharray: strokeStyle === 'dashed' ? '5,5' : undefined,
        opacity: 0.7
      }
    };

    // valueが必要な場合のみ設定
    if (type !== 'diagonal') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        line.value = numValue;
      } else {
        showError('Please enter a valid number');
        return;
      }
    }

    onAdd(line);
    onCancel();
  };

  return (
    <div className={styles.referenceLineForm}>
      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Type</label>
        <select 
          value={type} 
          onChange={(e) => setType(e.target.value as 'horizontal' | 'vertical' | 'diagonal')}
          className={styles.settingSelect}
        >
          <option value="horizontal">Horizontal (y = value)</option>
          <option value="vertical">Vertical (x = value)</option>
          <option value="diagonal">Diagonal (y = x)</option>
        </select>
      </div>

      {type !== 'diagonal' && (
        <div className={styles.settingItem}>
          <label className={styles.settingLabel}>
            Value {type === 'horizontal' ? '(y)' : '(x)'}
          </label>
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={styles.settingInput}
            placeholder="Enter numeric value"
          />
        </div>
      )}

      <div className={styles.settingItem}>
        <label className={styles.settingLabel}>Label (optional)</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className={styles.settingInput}
          placeholder="e.g., Target, Threshold"
        />
      </div>

      <div className={styles.settingsRow}>
        <div className={styles.settingItem}>
          <label className={styles.settingLabel}>Style</label>
          <select 
            value={strokeStyle} 
            onChange={(e) => setStrokeStyle(e.target.value as 'solid' | 'dashed')}
            className={styles.settingSelect}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
          </select>
        </div>
        
        <div className={styles.settingItem}>
          <label className={styles.settingLabel}>Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className={styles.colorInput}
          />
        </div>
      </div>

      <div className={styles.formButtons}>
        <button
          onClick={onCancel}
          className={styles.cancelButton}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className={styles.submitButton}
        >
          Add
        </button>
      </div>
    </div>
  );
}